import {
  Col,
  DatePicker,
  Form,
  Input,
  Paragraph,
  RadioGroup,
  Row,
  Select,
  Textarea,
  type FormInstance,
  type SelectOption,
} from "@ir/ui";
import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  ALL_PURCHASERS_SCOPE,
  SPECIFIC_PURCHASER_SCOPE,
  productPriceMinDate,
  type ProductPriceAddFormValues,
  type ProductPricePurchaserOption,
} from "./productPriceAddForm.shared";

interface MasterDataProductPriceAddFormProps {
  form: FormInstance<ProductPriceAddFormValues>;
  initialValues: ProductPriceAddFormValues;
  purchaserOptions: ProductPricePurchaserOption[];
  purchasersLoading?: boolean;
  minDate: string;
  onSubmit: (values: ProductPriceAddFormValues) => Promise<void> | void;
  onFormValidityChange?: (isSubmittable: boolean) => void;
  onTargetChange?: (appliesTo: string, purchaserId: number | null) => void;
}

const PRICE_ALREADY_STARTS_TODAY =
  "A price already starts today. Choose a later date.";

function parseSelectId(value: unknown): number | null {
  if (value == null || typeof value !== "object" || !("value" in value)) {
    return null;
  }
  const parsed = Number((value as SelectOption).value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function isValidPrice(raw: string): boolean {
  const trimmed = raw.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return false;
  const amount = Number(trimmed);
  return Number.isFinite(amount) && amount > 0;
}

function isTodayOrFuture(value: string, minDate: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && value >= minDate;
}

const PriceDateField = ({
  value,
  onChange,
  minDate,
}: Readonly<{
  value?: string;
  onChange?: (value: string) => void;
  minDate: string;
}>) => {
  const safeValue =
    value && isTodayOrFuture(value, minDate) ? value : undefined;
  return (
    <DatePicker
      key={minDate}
      value={safeValue}
      minValue={minDate}
      isNonModal
      shouldCloseOnInteractOutside
      dateFormat="DD MMM, YYYY"
      onChange={(next) => onChange?.(next)}
    />
  );
};

export const MasterDataProductPriceAddForm = ({
  form,
  initialValues,
  purchaserOptions,
  purchasersLoading = false,
  minDate,
  onSubmit,
  onFormValidityChange,
  onTargetChange,
}: Readonly<MasterDataProductPriceAddFormProps>) => {
  const todayBlocked = minDate > productPriceMinDate();
  const selectOptions = useMemo<SelectOption[]>(
    () =>
      purchaserOptions.map((purchaser) => ({
        value: String(purchaser.purchaserId),
        label: purchaser.purchaserName,
      })),
    [purchaserOptions],
  );

  const updateFormValidity = useCallback(() => {
    const hasErrors = form
      .getFieldsError()
      .some((field) => (field.errors?.length ?? 0) > 0);
    const values = form.getFieldsValue();
    const appliesTo = values.appliesTo || ALL_PURCHASERS_SCOPE;
    const purchaserId =
      values.purchaserId != null ? Number(values.purchaserId) : null;
    onTargetChange?.(appliesTo, purchaserId);
    const purchaserReady =
      appliesTo !== SPECIFIC_PURCHASER_SCOPE ||
      (purchaserId != null && purchaserId > 0);
    const ready =
      !hasErrors &&
      purchaserReady &&
      isValidPrice(values.productPrice ?? "") &&
      isTodayOrFuture(values.startDate ?? "", minDate) &&
      (values.notes ?? "").length <= 500;
    onFormValidityChange?.(ready);
  }, [form, minDate, onFormValidityChange, onTargetChange]);

  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    form.setFieldsValue(initialValues);
    updateFormValidity();
  }, [form, initialValues, updateFormValidity]);

  useEffect(() => {
    const startDate = String(form.getFieldValue("startDate") ?? "");
    const blockedToday = form
      .getFieldError("startDate")
      .includes(PRICE_ALREADY_STARTS_TODAY);
    if (startDate && !isTodayOrFuture(startDate, minDate)) {
      form.setFieldValue("startDate", "");
      form.setFields([
        {
          name: "startDate",
          errors: [PRICE_ALREADY_STARTS_TODAY],
        },
      ]);
    } else if (
      blockedToday &&
      (!startDate || isTodayOrFuture(startDate, minDate))
    ) {
      form.setFields([{ name: "startDate", errors: [] }]);
    }
    updateFormValidity();
  }, [form, minDate, updateFormValidity]);

  return (
    <Form
      form={form}
      onFinish={onSubmit}
      onFieldsChange={updateFormValidity}
      initialValues={initialValues}
      className="master-data-price-add-form"
    >
      <Paragraph className="m-0 mb-3 text-grey-600">
        The new price starts on the effective date and stays in place until the next change.
      </Paragraph>
      <Form.Item
        name="appliesTo"
        label="Applies to"
        colon={false}
        helpText="Base price for every purchaser, or a price for one purchaser."
        className="mt-5"
      >
        <AppliesToField
          onScopeChange={(next) => {
            if (next !== SPECIFIC_PURCHASER_SCOPE) {
              form.setFieldValue("purchaserId", null);
            }
            updateFormValidity();
          }}
        />
      </Form.Item>

      <Form.Item
        noStyle
        shouldUpdate={(prev, next) => prev.appliesTo !== next.appliesTo}
      >
        {() =>
          form.getFieldValue("appliesTo") === SPECIFIC_PURCHASER_SCOPE ? (
            <Form.Item
              name="purchaserId"
              label="Purchaser"
              colon={false}
              helpText="Overrides the base price for this purchaser only."
              rules={[
                {
                  validator: (_rule, value) => {
                    if (value == null || Number(value) <= 0) {
                      return Promise.reject(new Error("Select a purchaser."));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <PurchaserSelect
                options={selectOptions}
                isLoading={purchasersLoading}
              />
            </Form.Item>
          ) : null
        }
      </Form.Item>

      <Row>
        <Col span={6}>
          <Form.Item
            name="productPrice"
            label="New price"
            colon={false}
            helpText="Greater than 0, with up to 2 decimal places."
            rules={[
              { required: true, message: "Price is required." },
              {
                validator: (_rule, value: string) => {
                  if (value == null || value === "") return Promise.resolve();
                  if (!isValidPrice(value)) {
                    return Promise.reject(
                      new Error(
                        "Price must be greater than 0 and have at most 2 decimal places.",
                      ),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input prefix="$" placeholder="0.00" />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            name="startDate"
            label="Effective date"
            colon={false}
            helpText={
              todayBlocked
                ? PRICE_ALREADY_STARTS_TODAY
                : "Today or a later date."
            }
            rules={[
              { required: true, message: "Effective date is required." },
              {
                validator: (_rule, value: string) => {
                  if (!value) return Promise.resolve();
                  if (!isTodayOrFuture(value, productPriceMinDate())) {
                    return Promise.reject(
                      new Error("Effective date can't be before today."),
                    );
                  }
                  if (!isTodayOrFuture(value, minDate)) {
                    return Promise.reject(new Error(PRICE_ALREADY_STARTS_TODAY));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <PriceDateField minDate={minDate} />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name="notes"
        label="Note"
        colon={false}
        helpText="Optional. This note is shown in the price history."
        rules={[
          {
            max: 500,
            message: "Notes must not exceed 500 characters.",
          },
        ]}
      >
        <Textarea
          placeholder="Why this price is changing"
          minRows={3}
          maxLength={500}
          showCharCount
        />
      </Form.Item>
    </Form>
  );
};

const AppliesToField = ({
  value,
  onChange,
  onScopeChange,
}: Readonly<{
  value?: string;
  onChange?: (value: string) => void;
  onScopeChange?: (value: string) => void;
}>) => (
  <RadioGroup
    orientation="horizontal"
    value={value}
    options={[
      { label: "All purchasers (Base list)", value: ALL_PURCHASERS_SCOPE },
      { label: "Specific purchaser", value: SPECIFIC_PURCHASER_SCOPE },
    ]}
    onChange={(next) => {
      onChange?.(next);
      onScopeChange?.(next);
    }}
  />
);

const PurchaserSelect = ({
  value,
  onChange,
  options,
  isLoading,
}: Readonly<{
  value?: number | null;
  onChange?: (value: number | null) => void;
  options: SelectOption[];
  isLoading?: boolean;
}>) => {
  const selected = options.find((option) => option.value === String(value));
  return (
    <Select
      key={`price-purchaser-${value ?? "none"}-${options.length}`}
      size="medium"
      isSearchable
      isClearable={false}
      isLoading={isLoading}
      placeholder="Select purchaser..."
      options={options}
      defaultValue={selected}
      onChange={(next) => onChange?.(parseSelectId(next))}
      aria-label="Purchaser"
    />
  );
};
